// Administration → Étages & services: « Exporter la structure » (a JSON file to download, the rows
// of the tables on request) and « Importer une structure » (a file → a preview of what will be
// created or replaced → the import, « Ajouter » or « Remplacer toute la structure »).
// Endpoints (server/office/structure.js): GET /api/office/structure[?rows=1] ·
// POST /api/office/structure/preview {json, mode} · POST /api/office/structure/import {json, mode, confirm: true}.
import { h, field, input, select, switchToggle, icon } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import './strings.js';

export const IMPORT_MODES = Object.freeze(['add', 'replace']);

/** Lower-case ascii slug ("Maeck Office" → "maeck-office"), like the server's. */
function slug(s) {
  return String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40).replace(/-+$/g, '') || 'bureau';
}

/**
 * The name of the downloaded file: structure-<office>-<yyyy-mm-dd>.json.
 * @param {string} name the office's name
 * @param {Date} [at]
 */
export function structureFileName(name, at = new Date()) {
  return `structure-${slug(name)}-${at.toISOString().slice(0, 10)}.json`;
}

/**
 * Save `data` as a JSON file in the browser (Blob + a temporary link). False when the browser
 * cannot (no Blob / URL.createObjectURL).
 * @param {object} data
 * @param {string} fileName
 */
export function downloadJson(data, fileName) {
  const text = `${JSON.stringify(data, null, 2)}\n`;
  const B = globalThis.Blob;
  const U = globalThis.URL;
  const doc = globalThis.document;
  if (typeof B !== 'function' || typeof U?.createObjectURL !== 'function' || !doc?.body) return false;
  const url = U.createObjectURL(new B([text], { type: 'application/json' }));
  const a = h('a', { href: url, download: fileName, hidden: true });
  doc.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => { try { U.revokeObjectURL(url); } catch { /* ignore */ } }, 5000);
  return true;
}

/**
 * Text of a chosen file (File.text(), else FileReader).
 * @param {{text?: () => Promise<string>}} file
 * @returns {Promise<string>}
 */
export function readFileText(file) {
  if (typeof file?.text === 'function') return file.text();
  return new Promise((resolve, reject) => {
    const R = globalThis.FileReader;
    if (typeof R !== 'function') { reject(new Error(t('adm.structure.unreadable'))); return; }
    const reader = new R();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(new Error(t('adm.structure.unreadable')));
    reader.readAsText(file);
  });
}

/**
 * The lines of a preview (POST /api/office/structure/preview): what will be created, replaced,
 * renamed, and the warnings. Pure (tested).
 * @param {object} plan
 * @returns {{created: string, replaced: string|null, renamed: string|null, name: string|null, warnings: string[], problems: string[]}}
 */
export function planSummary(plan) {
  const c = plan?.counts || {};
  const created = t('adm.structure.created', {
    floors: c.floors ?? 0, departments: c.departments ?? 0, agents: c.agents ?? 0,
    routines: c.routines ?? 0, triggers: c.triggers ?? 0, tables: c.tables ?? 0,
  }) + (c.rows ? t('adm.structure.createdRows', { rows: c.rows }) : '');
  const r = plan?.replaced;
  const replaced = r ? t('adm.structure.replaced', {
    floors: r.floors ?? 0, departments: r.departments ?? 0, agents: r.agents ?? 0, routines: r.routines ?? 0, triggers: r.triggers ?? 0,
  }) : null;
  const moved = Array.isArray(plan?.remapped) ? plan.remapped : [];
  const renamed = moved.length ? t('adm.structure.renamed', {
    n: moved.length,
    list: moved.slice(0, 6).map((m) => `${m.from} → ${m.to}`).join(', ') + (moved.length > 6 ? '…' : ''),
  }) : null;
  return {
    created,
    replaced,
    renamed,
    name: plan?.name ? t('adm.structure.newName', { name: plan.name }) : null,
    warnings: Array.isArray(plan?.warnings) ? plan.warnings : [],
    problems: Array.isArray(plan?.problems) ? plan.problems : [],
  };
}

function setLabel(btn, label) {
  const span = [...(btn?.childNodes || [])].find((n) => n.classList?.contains('btn-label'));
  if (span) span.textContent = label;
}

/**
 * « Exporter la structure » : a modal (option « avec les données des tables »), then the download.
 * @param {object} ctx app context (api, toast, store)
 * @param {Function} openForm the admin form modal (see floors.js)
 */
export function openExport(ctx, openForm) {
  let withRows = false;
  const rows = switchToggle(t('adm.structure.withRows'), { onChange: (v) => { withRows = v; }, hint: t('adm.structure.withRowsHint') });
  return openForm({
    title: t('adm.structure.exportTitle'),
    submitLabel: t('adm.structure.download'),
    fields: [h('p', { class: 'adm-note' }, t('adm.structure.exportText')), rows],
    onSubmit: async () => {
      const data = await ctx.api.get(withRows || rows.input.checked ? '/api/office/structure?rows=1' : '/api/office/structure', { timeoutMs: 60_000 });
      const name = structureFileName(data?.name || ctx.store.get().office?.name);
      const save = typeof ctx.download === 'function' ? ctx.download : downloadJson;
      if (save(data, name) === false) throw new Error(t('adm.structure.noDownload'));
      const n = (k) => (Array.isArray(data?.[k]) ? data[k].length : 0);
      ctx.toast.success(t('adm.structure.exported', { floors: n('floors'), departments: n('departments'), agents: n('agents'), file: name }));
      return true;
    },
  });
}

/**
 * « Importer une structure » : a file and a mode → APERÇU (the preview is shown in the modal) →
 * IMPORTER (« Remplacer » asks for the office's name, like the reseed).
 * @param {object} ctx app context
 * @param {Function} openForm the admin form modal (see floors.js)
 * @param {() => void} afterChange reload the office after the import
 */
export function openImport(ctx, openForm, afterChange) {
  let plan = null;
  let text = null;
  let form = null;
  const officeName = ctx.store.get().office?.name || '';
  const fileInput = input({ type: 'file', accept: '.json,application/json', name: 'file', class: 'adm-file' });
  const fFile = field(t('adm.structure.file'), fileInput, { hint: t('adm.structure.fileHint') });
  const modeSel = select(IMPORT_MODES.map((m) => ({ value: m, label: t(`adm.structure.mode.${m}`) })), { value: 'add', name: 'mode' });
  const modeHint = h('p', { class: 'field-hint' }, t('adm.structure.modeHint.add'));
  const fMode = field(t('adm.structure.mode'), modeSel);
  fMode.appendChild(modeHint);
  const confirmInput = input({ value: '', placeholder: officeName, autocomplete: 'off', name: 'confirm' });
  const fConfirm = field(t('adm.reseed.confirmLabel'), confirmInput, { hint: t('adm.reseed.confirmHint', { name: officeName }) });
  fConfirm.hidden = true;
  const preview = h('div', { class: 'adm-structure-preview stack', role: 'status', 'aria-live': 'polite' });

  function reset() {
    plan = null;
    text = null;
    preview.replaceChildren();
    fConfirm.hidden = true;
    modeHint.textContent = t(`adm.structure.modeHint.${modeSel.value}`);
    if (form) setLabel(form.submitBtn, t('adm.structure.preview'));
  }
  fileInput.addEventListener('change', reset);
  modeSel.addEventListener('change', reset);

  function renderPlan(p) {
    const s = planSummary(p);
    const items = [];
    if (s.problems.length) {
      items.push(h('p', { class: 'adm-alert is-danger', role: 'alert' }, icon('alert', { size: 16 }), h('span', {}, t('adm.structure.invalid'))),
        h('ul', { class: 'adm-structure-problems' }, s.problems.map((x) => h('li', {}, x))));
    } else {
      items.push(h('p', { class: 'adm-structure-created' }, s.created));
      if (s.replaced) items.push(h('p', { class: 'adm-alert is-amber', role: 'note' }, icon('alert', { size: 16 }), h('span', {}, s.replaced)));
      if (s.name) items.push(h('p', { class: 'adm-note' }, s.name));
      if (s.renamed) items.push(h('p', { class: 'adm-note' }, s.renamed));
      if (s.warnings.length) items.push(h('ul', { class: 'adm-structure-warnings' }, s.warnings.map((x) => h('li', {}, x))));
    }
    preview.replaceChildren(...items);
  }

  form = openForm({
    title: t('adm.structure.importTitle'),
    submitLabel: t('adm.structure.preview'),
    danger: false,
    fields: [h('p', { class: 'adm-note' }, t('adm.structure.importText')), fFile, fMode, preview, fConfirm],
    onSubmit: async ({ err }) => {
      const mode = modeSel.value;
      if (!plan || !plan.ok) {
        const file = fileInput.files?.[0];
        if (!file) { fFile.setError(t('adm.structure.noFile')); err.set(t('adm.structure.noFile')); return false; }
        fFile.setError(null);
        text = await readFileText(file);
        plan = await ctx.api.post('/api/office/structure/preview', { json: text, mode }, { timeoutMs: 60_000 });
        renderPlan(plan);
        if (plan?.ok) {
          setLabel(form.submitBtn, t(mode === 'replace' ? 'adm.structure.doReplace' : 'adm.structure.doAdd'));
          if (mode === 'replace') { fConfirm.hidden = false; confirmInput.value = ''; }
        } else {
          plan = null;
        }
        return false;
      }
      if (mode === 'replace' && (!officeName || confirmInput.value.trim() !== officeName.trim())) {
        fConfirm.setError(t('adm.reseed.mismatch'));
        err.set(t('adm.reseed.mismatch'));
        return false;
      }
      const out = await ctx.api.post('/api/office/structure/import', { json: text, mode, confirm: true }, { timeoutMs: 120_000 });
      const c = out?.counts || {};
      ctx.toast.success(t(out?.backup ? 'adm.structure.doneReplace' : 'adm.structure.doneAdd', {
        floors: c.floors ?? 0, departments: c.departments ?? 0, agents: c.agents ?? 0, backup: out?.backup || '',
      }));
      for (const w of (out?.warnings || []).filter((x) => /non créée|non importées/.test(x))) ctx.toast.warn(w);
      afterChange?.();
      return true;
    },
  });
  return form;
}
